// src/components/settings/catalog/CatalogJobRunner.js
// CATALOG SCREEN VERIFIER: catscreen-7a2c4e91 — Settings › Catalog, Stage 4.
//
// Runs a bulk change to the end: calls runCatalogJob until the server says done, showing
// "Changing prices… 1,200 of 2,418". The server does the work in chunks and never changes an item
// twice, so every way out is safe:
//   - leave the page: the job is remembered in this browser (catalogApi.rememberJob); coming back
//     shows "A change didn't finish" with "Finish it";
//   - another tab is already running it ('aborted'): wait, then check again;
//   - the connection drops: "Continue" picks up where it stopped.
// Then a summary: changed, already that way, and the skipped items by name and reason.
// catscreen-7a2c4e91 Stage 6 — imports (action 'import') and their undos ('importUndo') run here too:
// an import's summary splits updated / new (job.newCount, from its preview); an undo's lists the
// items left alone because they were edited since, and the items archived because the import made them.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { jobProgressLine } from '../../../utils/catalogUi';
import { runCatalogJob, rememberJob, forgetJob, friendlyError, loadUndoArchived } from './catalogApi';
import { BTN_GHOST, BTN_PRIMARY, Dialog, ErrorLine } from './catalogUiParts';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const WAIT_MS = 15000;

const SKIP_GROUPS = [
    ['changed-since', 'Changed since you looked — left as they were'],
    ['edited-since', 'Edited since the import — left as they are'],
    ['no-cost', 'Skipped — no cost, so no markup to set'],
    ['no-price', 'Skipped — no price to change'],
    ['gone', 'No longer in the catalog'],
    ['too-many-folders', 'Skipped — already in 50 folders'],
];

const doneTitle = (action) => (action === 'archive' ? 'Items archived' : action === 'moveToFolder' ? 'Items moved'
    : action === 'addToFolder' ? 'Items added to the folder' : action === 'import' ? 'Import finished'
        : action === 'importUndo' ? 'Import undone' : 'Prices changed');

export function useCatalogJob({ orgId, onFinished }) {
    const [state, setState] = useState(null); // null | { phase, job, processed, result, error }
    const alive = useRef(true);
    // Set back on at every mount: React's StrictMode (development) mounts, unmounts and mounts again.
    useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

    const drive = useCallback(async (job) => {
        let processed = job.processed || 0;
        rememberJob(orgId, { ...job, processed });
        setState({ phase: 'running', job, processed });
        let waits = 0;
        for (;;) {
            if (!alive.current) return;
            try {
                const r = await runCatalogJob(job.jobId);
                if (!alive.current) return;
                processed = r.processed;
                if (r.done) {
                    forgetJob(orgId);
                    setState({ phase: 'done', job, processed, result: r });
                    return;
                }
                rememberJob(orgId, { ...job, processed });
                setState({ phase: 'running', job, processed });
            } catch (err) {
                if (!alive.current) return;
                const code = String((err && err.code) || '');
                if (/aborted/.test(code) && waits < 40) {
                    waits++;
                    setState({ phase: 'waiting', job, processed });
                    await sleep(WAIT_MS);
                    continue;
                }
                if (/not-found|failed-precondition|permission-denied/.test(code)) {
                    forgetJob(orgId);
                    setState({ phase: 'failed', job, processed, error: friendlyError(err, "This change can't carry on.") });
                    return;
                }
                console.error('Catalog: bulk change stopped', err);
                setState({ phase: 'paused', job, processed, error: friendlyError(err, 'The change stopped part-way. Press Continue to carry on — nothing is changed twice.') });
                return;
            }
        }
    }, [orgId]);

    const close = useCallback(() => {
        const finished = state && state.phase === 'done' ? state.result : null;
        setState(null);
        onFinished(finished, state ? state.job : null);
    }, [state, onFinished]);

    return { state, run: drive, close, orgId };
}

export function JobDialog({ runner }) {
    const st = runner.state;
    if (!st) return null;
    const { job } = st;
    const working = st.phase === 'running' || st.phase === 'waiting';
    const pct = job.total ? Math.min(100, Math.round(((st.processed || 0) / job.total) * 100)) : 0;
    return (
        <Dialog open title={st.phase === 'done' ? doneTitle(job.action) : 'Working on it'} onClose={working ? undefined : runner.close} busy={working}
            footer={working ? null : (
                <>
                    {st.phase === 'paused' && <button type="button" className={BTN_PRIMARY} onClick={() => runner.run({ ...job, processed: st.processed })}>Continue</button>}
                    <button type="button" className={st.phase === 'done' ? BTN_PRIMARY : BTN_GHOST} onClick={runner.close}>{st.phase === 'paused' ? 'Finish later' : 'Close'}</button>
                </>
            )}>
            {st.phase !== 'done' && (
                <div className="space-y-3">
                    <p className="text-sm text-gray-800 flex items-center gap-2" aria-live="polite">
                        {working && <Loader2 size={16} className="animate-spin text-[#5F4EC9]" aria-hidden="true" />}
                        {jobProgressLine(job.action, st.processed, job.total)}
                    </p>
                    <div className="h-2 rounded-full bg-gray-100 overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                        <div className="h-full bg-[#5F4EC9] transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    {st.phase === 'waiting' && <p className="text-sm text-gray-600">This change is still running from another tab or a moment ago. Checking again shortly…</p>}
                    {working && <p className="text-xs text-gray-500">You can leave this page. Come back to Catalog and choose “Finish it” — nothing is changed twice.</p>}
                    {st.phase === 'paused' && <ErrorLine text={st.error} />}
                    {st.phase === 'paused' && <p className="text-xs text-gray-500">“Finish later” keeps a reminder on the Catalog page in this browser.</p>}
                    {st.phase === 'failed' && <ErrorLine text={st.error} />}
                </div>
            )}
            {st.phase === 'done' && <Summary result={st.result} job={job} orgId={runner.orgId} />}
        </Dialog>
    );
}

function Summary({ result, job, orgId }) {
    const c = result.counts || {};
    const skipped = Array.isArray(result.skipped) ? result.skipped : [];
    const fmt = (n) => (n || 0).toLocaleString('en-US');
    const isImport = job && job.action === 'import';
    const isUndo = job && job.action === 'importUndo';
    const created = isImport && typeof job.newCount === 'number' ? Math.min(job.newCount, c.changed || 0) : null;
    const archived = useUndoArchived(isUndo ? orgId : null, isUndo ? job.jobId : null);
    let headline = `Changed: ${fmt(c.changed)}`;
    if (created !== null) headline = `Updated: ${fmt((c.changed || 0) - created)} · New items: ${fmt(created)}`;
    if (isUndo) headline = archived.count === null ? `Undone: ${fmt(c.changed)}` : `Put back as they were: ${fmt((c.changed || 0) - archived.count)}`;
    return (
        <div className="space-y-3">
            <p className="flex items-center gap-2 text-sm font-medium text-green-800"><CheckCircle2 size={18} /> {headline}</p>
            {isUndo && archived.count > 0 && (
                <div>
                    <h3 className="text-sm font-semibold text-gray-800">Archived, because the import added them ({fmt(archived.count)})</h3>
                    <ul className="mt-1 max-h-40 overflow-y-auto text-sm text-gray-700 list-disc pl-5 space-y-0.5">
                        {archived.items.map((x) => <li key={x.id}>{x.name || '(no name)'}</li>)}
                    </ul>
                    {archived.count > archived.items.length && <p className="text-xs text-gray-500">Showing the first {fmt(archived.items.length)}.</p>}
                    <p className="text-xs text-gray-500 mt-1">Archived items aren't deleted. You can find and restore them under Archived.</p>
                </div>
            )}
            {isUndo && archived.error && <p className="text-xs text-gray-500">{archived.error}</p>}
            {c.unchanged > 0 && <p className="text-sm text-gray-700">Already that way: {fmt(c.unchanged)}</p>}
            {c.skipped > 0 && <p className="text-sm text-amber-800">Left alone: {fmt(c.skipped)}</p>}
            {SKIP_GROUPS.map(([reason, label]) => {
                const list = skipped.filter((x) => x.reason === reason);
                if (!list.length) return null;
                return (
                    <div key={reason}>
                        <h3 className="text-sm font-semibold text-gray-800">{label} ({fmt(list.length)})</h3>
                        <ul className="mt-1 max-h-40 overflow-y-auto text-sm text-gray-700 list-disc pl-5 space-y-0.5">
                            {list.map((x) => <li key={x.id}>{x.name || '(no name)'}</li>)}
                        </ul>
                    </div>
                );
            })}
            {c.skipped > skipped.length && <p className="text-xs text-gray-500">Showing the first {fmt(skipped.length)} items left alone.</p>}
            <p className="text-xs text-gray-500">The list updates in a few seconds.</p>
        </div>
    );
}

// catscreen-7a2c4e91 Stage 6 — the items an undo archived (from their history entries).
function useUndoArchived(orgId, undoJobId) {
    const [st, setSt] = useState({ count: null, items: [], error: '' });
    useEffect(() => {
        if (!orgId || !undoJobId) return undefined;
        let live = true;
        loadUndoArchived(orgId, undoJobId)
            .then((r) => { if (live) setSt({ count: r.count, items: r.items, error: '' }); })
            .catch((err) => {
                console.error('Catalog: archived list failed', err);
                if (live) setSt({ count: null, items: [], error: "Couldn't list the archived items. They're under Archived in the Catalog." });
            });
        return () => { live = false; };
    }, [orgId, undoJobId]);
    return st;
}

/** "A change didn't finish" — shown on the Items tab when this browser remembers an unfinished job. */
export function UnfinishedJobBanner({ job, onFinish, onDismiss }) {
    if (!job) return null;
    return (
        <div role="status" className="flex flex-wrap items-center gap-2 p-3 rounded-lg border border-amber-300 bg-amber-50 text-sm text-amber-900">
            <span className="flex-1 min-w-[12rem]">
                A change didn't finish ({jobProgressLine(job.action, job.processed, job.total).replace('… ', ': ')} done).
                <span className="block text-xs text-amber-800 mt-0.5">Dismissing only hides this reminder. It doesn't undo what was already changed.</span>
            </span>
            <button type="button" className={BTN_PRIMARY} onClick={onFinish}>Finish it</button>
            <button type="button" className={BTN_GHOST} onClick={onDismiss}>Dismiss</button>
        </div>
    );
}
